// Row contracts of the assessment rows (M8-ASSESS; 02 §Assessment; x-ray.md §11.2): rounds,
// participants (token hash omitted: it never leaves the server), responses, report revisions,
// proposals. Same conventions as `rows.ts`; `contract-drift.test.ts` binds each to its table.
// The shapes inside the jsonb columns (answers, report body, proposal payload) are typed in
// `api/assess.ts`.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  AssessmentParticipantKind,
  AssessmentParticipantState,
  AssessmentProposalKind,
  AssessmentProposalState,
  AssessmentQuestionnaire,
  AssessmentRoundState,
} from "./enums";
import { Timestamp, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

export const AssessmentRound = z
  .object({
    company_id: Uuid,
    id: Uuid,
    number: z.int().min(1),
    name: z.string(),
    questionnaire: AssessmentQuestionnaire,
    state: AssessmentRoundState,
    invite_expiry_days: z.int().min(1).max(60),
    scoring_version: z.string(),
    opened_at: Timestamp.nullable(),
    closed_at: Timestamp.nullable(),
    reported_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AssessmentRound" });
export type AssessmentRound = z.infer<typeof AssessmentRound>;

export const AssessmentParticipant = z
  .object({
    company_id: Uuid,
    id: Uuid,
    round_id: Uuid,
    kind: AssessmentParticipantKind,
    membership_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    reports_to_seat_id: Uuid.nullable(),
    email: z.string().nullable(),
    display_name: z.string(),
    role_title: z.string().nullable(),
    is_owner: z.boolean(),
    state: AssessmentParticipantState,
    expires_at: Timestamp.nullable(),
    reminder_count: z.int().min(0),
    last_reminded_at: Timestamp.nullable(),
    opened_at: Timestamp.nullable(),
    completed_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AssessmentParticipant" });
export type AssessmentParticipant = z.infer<typeof AssessmentParticipant>;

export const AssessmentResponseRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    round_id: Uuid,
    participant_id: Uuid,
    answers: z.record(z.string(), z.unknown()),
    submitted_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AssessmentResponseRow" });

export const AssessmentReportRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    round_id: Uuid,
    version: z.int().min(1),
    scoring_version: z.string(),
    body: z.record(z.string(), z.unknown()),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "AssessmentReportRow" });

export const AssessmentAppliedRef = z
  .object({
    seat_id: Uuid.optional(),
    role_id: Uuid.optional(),
    responsibility_id: Uuid.optional(),
    metric_def_id: Uuid.optional(),
  })
  .meta({ id: "AssessmentAppliedRef" });

export const AssessmentProposalRow = z
  .object({
    company_id: Uuid,
    id: Uuid,
    round_id: Uuid,
    report_id: Uuid,
    key: z.string(),
    kind: AssessmentProposalKind,
    title: z.string(),
    detail: z.string(),
    depends_on: z.string().nullable(),
    payload: z.record(z.string(), z.unknown()),
    state: AssessmentProposalState,
    applied_ref: AssessmentAppliedRef.nullable(),
    applied_at: Timestamp.nullable(),
    position: z.int(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "AssessmentProposalRow" });
